<script lang="ts">
import {
  ref,
  computed,
  defineComponent,
  watch,
  Teleport,
  toRef,
  mergeProps,
  h,
  Fragment,
  withDirectives,
  vShow,
  unref,
  onBeforeUnmount
} from 'vue';

import { ClickOutside } from '@components/directives';
import { animate, classNames, convertToUnit, getScrollParent, IN_BROWSER } from '@components/utils';

import {
  CdsTransition,
  makeDelayProps,
  makeDimensionProps,
  makeLazyProps,
  makeOverlayProps,
  makeTransitionProps,
  useBackButton,
  useDimensions,
  useHydration,
  useLazy,
  useProxyModel,
  useRender,
  useRouter,
  useScopeId,
  useToggleScope
} from '../../composable';

import {
  CdsScrim,
  makeActivatorProps,
  makeLocationStrategyProps,
  makeScrollStrategyProps,
  useActivator,
  useLocationStrategies,
  useScrollStrategies,
  useStack,
  useTeleport
} from './composable';

import type { PropType } from 'vue';

export const componentName = 'CdsOverlay';
export default defineComponent({
  name: componentName,
  // @ts-ignore https://github.com/vuejs/core/issues/5833
  directives: { ClickOutside },
  inheritAttrs: false,
  props: {
    /**
     * Применяет position: absolute к содержимому оверлея
     */
    absolute: {
      type: Boolean as PropType<boolean>,
      default: false
    },

    ...makeOverlayProps(),
    ...makeActivatorProps(),
    ...makeDelayProps(),
    ...makeDimensionProps(),
    ...makeLocationStrategyProps(),
    ...makeScrollStrategyProps(),
    ...makeLazyProps(),
    ...makeTransitionProps(),

    _disableGlobalStack: {
      type: Boolean,
      default: undefined
    }
  },
  emits: {
    /**
     * Событие при изменении значения (v-model)
     * @type { boolean }
     */
    'update:modelValue': (value: boolean) => true,
    /**
     * Событие, которое срабатывает при клике вне оверлея.
     */
    'click:outside': (event: MouseEvent) => true,
    /**
     * Событие после появления оверлея
     */
    afterEnter: () => true,
    /**
     * Событие после исчезновения оверлея
     */
    afterLeave: () => true
  },
  setup(props, { attrs, emit, slots, expose }) {
    const model = useProxyModel(props, 'modelValue');
    const isActive = computed({
      get: () => model.value,
      set: (value) => {
        if (!(value && props.disabled)) {
          model.value = value;
        }
      }
    });
    const { teleportTarget } = useTeleport(
      computed(() => props.attach || props.contained),
      'cds-overlay-container'
    );
    const { hasContent, onAfterLeave: _onAfterLeave } = useLazy(props, isActive);
    const { globalTop, localTop, stackStyles } = useStack(isActive, toRef(props, 'zIndex'), props._disableGlobalStack);
    const { activatorEl, activatorRef, target, targetEl, targetRef, activatorEvents, contentEvents, scrimEvents } =
      useActivator(props, {
        isActive,
        isTop: localTop
      });
    const { dimensionsStyles } = useDimensions(props);
    const isMounted = useHydration();
    const { scopeId } = useScopeId();

    watch(
      () => props.disabled,
      (value) => {
        if (value) {
          isActive.value = false;
        }
      }
    );

    const root = ref<HTMLElement>();
    const scrimEl = ref<HTMLElement>();
    const contentEl = ref<HTMLElement>();
    const { contentStyles, updateLocation } = useLocationStrategies(props, {
      contentEl,
      activatorEl,
      target,
      isActive
    });

    useScrollStrategies(props, { root, contentEl, targetEl, isActive, updateLocation });

    const overlayClasses = computed(() => ({
      'cds-overlay--active': unref(isActive),
      'cds-overlay--absolute': props.absolute || props.contained,
      'cds-overlay--contained': props.contained
    }));

    const overlayStyles = computed(() => [
      unref(stackStyles),
      { '--cds-overlay-opacity': props.opacity, top: convertToUnit(unref(top)) }
    ]);

    IN_BROWSER &&
      watch(
        isActive,
        (value) => {
          if (value) {
            window.addEventListener('keydown', onKeydown);
          } else {
            window.removeEventListener('keydown', onKeydown);
          }
        },
        { immediate: true }
      );

    onBeforeUnmount(() => {
      if (!IN_BROWSER) return;

      window.removeEventListener('keydown', onKeydown);
    });

    const top = ref<number>();
    watch(
      () => isActive.value && (props.absolute || props.contained) && teleportTarget.value == null,
      (value) => {
        if (value) {
          const scrollParent = getScrollParent(root.value);
          if (scrollParent && scrollParent !== document.scrollingElement) {
            top.value = scrollParent.scrollTop;
          }
        }
      }
    );

    const router = useRouter();
    useToggleScope(
      () => props.closeOnBack,
      () => {
        useBackButton(router, (next) => {
          if (globalTop.value && isActive.value) {
            next(false);

            if (!props.persistent) {
              isActive.value = false;
            } else {
              animateClick();
            }
          } else {
            next();
          }
        });
      }
    );

    function onClickOutside(event: MouseEvent) {
      emit('click:outside', event);

      if (!props.persistent) {
        isActive.value = false;
      } else {
        animateClick();
      }
    }

    function closeConditional(event: Event) {
      return isActive.value && globalTop.value && (!props.scrim || event.target === unref(scrimEl));
    }

    function onKeydown(event: KeyboardEvent) {
      if (event.key === 'Escape' && globalTop.value) {
        if (!props.persistent) {
          isActive.value = false;

          if (contentEl.value?.contains(document.activeElement)) {
            activatorEl.value?.focus();
          }
        } else {
          animateClick();
        }
      }
    }

    function onAfterEnter() {
      emit('afterEnter');
    }

    function onAfterLeave() {
      _onAfterLeave();
      emit('afterLeave');
    }

    function animateClick() {
      if (props.noClickAnimation) return;

      contentEl.value &&
        animate(
          contentEl.value,
          [{ transformOrigin: 'center' }, { transform: 'scale(1.03)' }, { transformOrigin: 'center' }],
          {
            duration: 150, // @TODO need implement and use cds duration
            easing: 'cubic-bezier(0.4, 0, 0.2, 1)' // @TODO need implement and use cds easing
          }
        );
    }

    useRender(() =>
      h(Fragment, {}, [
        slots.activator?.({
          isActive: unref(isActive),
          props: mergeProps({ ref: activatorRef, targetRef }, unref(activatorEvents), props.activatorProps)
        }),

        isMounted.value &&
          h(Teleport, { disabled: !unref(teleportTarget), to: unref(teleportTarget) }, [
            hasContent.value &&
              h(
                'div',
                {
                  ref: root,
                  ...attrs,
                  class: classNames('cds-overlay', unref(overlayClasses), attrs.class),
                  style: unref(overlayStyles),
                  ...scopeId
                },
                [
                  h(CdsScrim, {
                    ref: scrimEl,
                    modelValue: unref(isActive) && !!props.scrim,
                    ...unref(scrimEvents)
                  }),
                  h(
                    CdsTransition,
                    {
                      appear: true,
                      persisted: true,
                      transition: props.transition,
                      onAfterEnter,
                      onAfterLeave
                    },
                    {
                      default: () =>
                        withDirectives(
                          h(
                            'div',
                            {
                              ref: contentEl,
                              class: ['cds-overlay__content', props.contentClasses],
                              style: [unref(dimensionsStyles), unref(contentStyles)],
                              ...unref(contentEvents),
                              ...props.contentProps
                            },
                            slots.default?.({ isActive })
                          ),
                          [
                            [vShow, unref(isActive)],
                            [
                              ClickOutside,
                              { handler: onClickOutside, closeConditional, include: () => [unref(activatorEl)] }
                            ]
                          ]
                        )
                    }
                  )
                ]
              )
          ])
      ])
    );

    expose({
      activatorEl,
      contentEl,
      globalTop,
      localTop,
      updateLocation
    });

    return {};
  }
});
</script>

<style lang="scss">
@import './CdsOverlay';
</style>
